// Inbox's views' props, as zod (pages/ux-full components/inbox.js: AgentRequest, NoteList). Each is
// drawn from ready-to-show words the office builds from real answers: one approvals.list row (its
// agent's name, its input in words), and notifications.list's rows grouped as the bell groups them.
// Neither is in TOOL_VIEWS: no one tool's answer is these props yet.
import { z } from 'zod';

const Id = z.string().min(1);

export const REQUEST_STATES = ['pending', 'approved', 'declined', 'expired'] as const;

/** One approvals.list row: what an agent or the person's own AI tool asked to do, exactly as it runs. */
export const AgentRequestProps = z.object({
  id: Id,
  /** actionTitle: "Pay vendor bills". */
  title: z.string(),
  /** The action's dotted name (vendorBills.pay), on hover. */
  action: z.string(),
  /** Who asked: an agent by its name ("Accounting agent"), or the person's own AI tool (tool: true). */
  asker: z.object({ name: z.string(), tool: z.boolean() }),
  /** When it was asked, in words: "Oct 23, 7:10 p.m." */
  asked: z.string(),
  /** When it lapses if nobody decides, in words; null once decided. */
  expires: z.string().nullable(),
  explanation: z.string().nullable(),
  /** The run that asked found what read like instructions in customer text (its flags). */
  flagged: z.boolean(),
  /** A money request's kind ("Vendor payment"): approving it also needs the owner's grant for it. */
  money: z.string().nullable(),
  /** Exactly what will run, in words, with the exact value on hover; null: shown only to those who could do it. */
  rows: z.array(z.object({ label: z.string(), text: z.string(), exact: z.string().optional() })).nullable(),
  state: z.enum(REQUEST_STATES),
  /** How it was decided, once it was. */
  decided: z
    .object({
      /** In words: "Oct 23, 7:12 p.m." */
      at: z.string().nullable(),
      /** The note the agent reads next time (a decline's). */
      note: z.string().nullable(),
      /** An approved one: it ran, is still running, or failed (and why). */
      ran: z.enum(['done', 'running', 'failed']).nullable(),
      failed: z.string().nullable(),
    })
    .nullable(),
  /** Null when this person may decide it; otherwise why not, in plain words. */
  cannotDecide: z.string().nullable(),
  /** The person's own AI tool's request: approving can also let that connection act as them for a while. */
  canTrust: z.boolean(),
  trustHours: z.int().min(1),
});
export type AgentRequestProps = z.infer<typeof AgentRequestProps>;

/** notifications.list's rows, grouped as the bell groups them: Needs you now, Earlier today, Earlier. */
export const NoteListProps = z.object({
  groups: z.array(
    z.object({
      key: z.enum(['now', 'today', 'earlier']),
      label: z.string(),
      notes: z.array(
        z.object({
          id: Id,
          title: z.string(),
          body: z.string().nullable(),
          /** "10:42 a.m." today, "Yest.", else "Oct 14" */
          time: z.string(),
          urgent: z.boolean(),
          read: z.boolean(),
        }),
      ),
    }),
  ),
});
export type NoteListProps = z.infer<typeof NoteListProps>;
